import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { noteTakerStore } from 'shared/stores/noteTakerStore'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import { ReactComponent as QuestionCircleIcon } from 'assets/icons/question-circle.svg'
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg'
import { ReactComponent as PauseIcon } from 'assets/icons/pause.svg'
import { ReactComponent as PlayIcon } from 'assets/icons/play.svg'
import { ReactComponent as ArrowsOpened } from 'assets/icons/arrows-opened.svg'
import { ReactComponent as ArrowsClosed } from 'assets/icons/arrows-closed.svg'
import {
	WIDGET_COMPACT_HEIGHT,
	WIDGET_EXPANDED_HEIGHT,
	WIDGET_WIDTH,
	WIDGET_DEFAULT_TOP_OFFSET,
} from './utils/widgetLayout'
import {
	fitWidgetPosition,
	getDefaultWidgetPositionUnderTrigger,
} from './utils/fitWidgetPosition'
import { useNoteTakerWidgetDrag } from './hooks/useNoteTakerWidgetDrag'
import s from './NoteTakerDashboardWidget.module.scss'

const WAVE_BARS = Array.from({ length: 48 }, (_, index) => index)

const INITIAL_WIDGET_POSITION = {
	left: 0,
	verticalAnchor: 'top',
	verticalOffset: WIDGET_DEFAULT_TOP_OFFSET,
}

function getWidgetHeight(isExpanded, isProcessing) {
	if (isProcessing) return WIDGET_COMPACT_HEIGHT
	return isExpanded ? WIDGET_EXPANDED_HEIGHT : WIDGET_COMPACT_HEIGHT
}

const SavingSessionWidget = ({ student, step }) => {
	let label = 'Preparing...'
	if (step === 'uploading_audio') {
		label = 'Uploading audio...'
	} else if (step === 'creating_note') {
		label = 'Saving session...'
	} else if (step === 'draining_transcript') {
		label = 'Finalizing transcript...'
	}

	return (
		<div className={s.processingCard}>
			<div className={s.processingOrb}>
				<div className={s.processingRing} />
				<CheckIcon />
			</div>
			<div className={s.processingTitle}>{label}</div>
			<div className={s.processingText}>
				{noteTakerStore.recordingLabel}
				{student ? ` - ${student.name}` : ''}
			</div>
			<div className={s.progressTrack}>
				<span className={s.progressIndeterminate} />
			</div>
		</div>
	)
}

const FailedSessionWidget = ({ error }) => (
	<div className={s.processingCard}>
		<div className={s.processingTitle}>Could not save session</div>
		<div className={s.processingText}>
			{error?.message || 'Something went wrong while saving this recording.'}
		</div>
		<div className={s.failedActions}>
			<button
				type="button"
				className={s.cancelButton}
				onClick={() => noteTakerStore.discardFailedSession()}
			>
				Discard
			</button>
			<button
				type="button"
				className={s.finishButton}
				onClick={() => noteTakerStore.retrySessionCreation()}
			>
				Try again
			</button>
		</div>
	</div>
)

const ProcessingWidget = ({ student, aiStep, finalizing = false }) => {
	const structuringClass =
		aiStep === 'processing'
			? s.processingItemWorking
			: aiStep === 'completed'
				? s.processingItemDone
				: s.processingItemPending

	return (
		<div className={s.processingCard}>
			<div className={s.processingOrb}>
				<div className={s.processingRing} />
				<CheckIcon />
			</div>
			<div className={s.processingTitle}>
				{finalizing ? 'Finishing note...' : 'Koda is converting...'}
			</div>
			<div className={s.processingText}>
				{noteTakerStore.recordingLabel}
				{student ? ` - ${student.name}` : ''}
			</div>
			<div className={s.progressTrack}>
				<span
					className={
						aiStep === 'processing' ? s.progressIndeterminate : undefined
					}
				/>
			</div>
			<ul className={s.processingList}>
				<li className={s.processingItemDone}>Audio transcribed</li>
				<li className={s.processingItemDone}>
					{student ? `Student matched: ${student.name}` : 'Student matching'}
				</li>
				<li className={structuringClass}>
					{aiStep === 'queued' || aiStep === 'polling'
						? 'Preparing note...'
						: 'Structuring note...'}
				</li>
			</ul>
		</div>
	)
}

const FailedAiWidget = ({ error }) => (
	<div className={s.processingCard}>
		<div className={s.processingTitle}>Could not structure note</div>
		<div className={s.processingText}>
			{error?.message || 'Koda could not finish processing this session.'}
		</div>
		<div className={s.failedActions}>
			<button
				type="button"
				className={s.cancelButton}
				onClick={() => noteTakerStore.discardFailedAiProcessing()}
			>
				Discard
			</button>
			<button
				type="button"
				className={s.finishButton}
				onClick={() => noteTakerStore.retryAiProcessing()}
			>
				Try again
			</button>
		</div>
	</div>
)

const NoteTakerDashboardWidget = observer(() => {
	const [widgetPosition, setWidgetPosition] = useState(INITIAL_WIDGET_POSITION)
	const widgetRef = useRef(null)
	const transcriptScrollRef = useRef(null)
	const hasPlacedUnderTriggerRef = useRef(false)

	const isExpanded = noteTakerStore.widgetMode === 'expanded'
	const showSavingSession = noteTakerStore.isSubmittingSession
	const showSessionFailed = noteTakerStore.isSessionCreationFailed
	const showAiFailed = noteTakerStore.isAiProcessingFailed
	const showAiProcessing =
		noteTakerStore.isSessionCreationComplete &&
		noteTakerStore.isProcessing &&
		noteTakerStore.isAiProcessingActive
	const showFinalizing = noteTakerStore.isFinalizingSession
	const showProcessing =
		showSavingSession ||
		showSessionFailed ||
		showAiProcessing ||
		showAiFailed ||
		showFinalizing
	const widgetHeight = getWidgetHeight(isExpanded, showProcessing)

	const { isDragging, isDraggingRef, onDragHandlePointerDown } =
		useNoteTakerWidgetDrag({
			widgetRef,
			widgetPosition,
			widgetHeight,
			onPositionChange: setWidgetPosition,
		})

	const getRailRect = () =>
		widgetRef.current?.parentElement?.getBoundingClientRect()

	useLayoutEffect(() => {
		if (!noteTakerStore.shouldShowWidget) {
			hasPlacedUnderTriggerRef.current = false
			return
		}
		if (isDraggingRef.current || !widgetRef.current) return

		const railRect = getRailRect()
		if (!railRect) return

		setWidgetPosition(prev => {
			const base = hasPlacedUnderTriggerRef.current
				? prev
				: getDefaultWidgetPositionUnderTrigger(railRect, widgetHeight)
			hasPlacedUnderTriggerRef.current = true
			return fitWidgetPosition(
				base,
				widgetHeight,
				railRect.width,
				railRect.height,
			)
		})
	}, [isExpanded, showProcessing, widgetHeight, isDraggingRef])

	useEffect(() => {
		const handleResize = () => {
			if (!widgetRef.current) return
			const railRect = getRailRect()
			if (!railRect) return
			setWidgetPosition(prev =>
				fitWidgetPosition(prev, widgetHeight, railRect.width, railRect.height),
			)
		}

		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [widgetHeight])

	useEffect(() => {
		const el = transcriptScrollRef.current
		if (!el) return
		el.scrollTop = el.scrollHeight
	}, [])

	if (!noteTakerStore.shouldShowWidget) return null

	const student = noteTakerStore.assignedStudent
	const isFinishDisabled =
		noteTakerStore.isSubmittingSession || noteTakerStore.isProcessing
	const showStudentRow = Boolean(student) && !showProcessing
	const showMissingStudent = !student && !showProcessing
	const studentSubline = student
		? [
				student.grade ? `Grade ${student.grade}` : 'Student',
				noteTakerStore.isStudentManuallySelected ? null : 'detected',
			]
				.filter(Boolean)
				.join(' - ')
		: ''
	const showTranscript = !showProcessing
	const transcriptText = noteTakerStore.transcriptPreview.trim()
	const isTranscribing = noteTakerStore.transcriptSttInFlight > 0

	const positionStyle =
		widgetPosition.verticalAnchor === 'bottom'
			? { bottom: `${widgetPosition.verticalOffset}px` }
			: { top: `${widgetPosition.verticalOffset}px` }

	return (
		<aside
			ref={widgetRef}
			className={[
				s.widget,
				isDragging ? s.widgetDragging : '',
				!isExpanded ? s.widgetCompact : '',
				noteTakerStore.isPaused ? s.widgetPaused : '',
				showProcessing ? s.widgetProcessing : '',
			]
				.filter(Boolean)
				.join(' ')}
			style={{
				position: 'absolute',
				left: `${widgetPosition.left}px`,
				...positionStyle,
				width: WIDGET_WIDTH,
				margin: 0,
				zIndex: isDragging ? 9999 : undefined,
			}}
		>
			<div
				className={s.dragHandle}
				aria-label="Drag recording widget"
				onPointerDown={onDragHandlePointerDown}
			>
				<span />
			</div>

			{showSavingSession ? (
				<SavingSessionWidget
					student={student}
					step={noteTakerStore.sessionCreationStatus}
				/>
			) : showSessionFailed ? (
				<FailedSessionWidget error={noteTakerStore.sessionCreationError} />
			) : showAiFailed ? (
				<FailedAiWidget error={noteTakerStore.aiProcessingError} />
			) : showAiProcessing ? (
				<ProcessingWidget
					student={student}
					aiStep={noteTakerStore.aiProcessingStatus}
				/>
			) : showFinalizing ? (
				<ProcessingWidget student={student} aiStep="completed" finalizing />
			) : (
				<>
					<div className={s.header}>
						<div className={s.titleRow}>
							<div className={s.titleLeft}>
								<span className={s.statusDot} />
								<span className={s.title}>
									{noteTakerStore.isPaused ? 'Paused' : 'Recording'}
								</span>
								<span className={s.time}>{noteTakerStore.recordingLabel}</span>
							</div>

							<div className={s.actions}>
								<button
									type="button"
									className={s.iconButton}
									onClick={() =>
										noteTakerStore.isPaused
											? noteTakerStore.resumeRecording()
											: noteTakerStore.pauseRecording()
									}
									aria-label={
										noteTakerStore.isPaused
											? 'Resume recording'
											: 'Pause recording'
									}
								>
									{noteTakerStore.isPaused ? <PlayIcon /> : <PauseIcon />}
								</button>
								<button
									type="button"
									className={s.iconButton}
									onClick={() => {
										if (isExpanded) {
											noteTakerStore.shrinkWidget()
										} else {
											noteTakerStore.expandWidget()
										}
									}}
									aria-label={isExpanded ? 'Collapse widget' : 'Expand widget'}
								>
									{isExpanded ? <ArrowsClosed /> : <ArrowsOpened />}
								</button>
							</div>
						</div>
					</div>

					<div className={s.wave}>
						<div className={s.waveBars}>
							{WAVE_BARS.map(index => {
								const t = index / (WAVE_BARS.length - 1)
								const r = Math.round(196 + (252 - 196) * t)
								const g = Math.round(168 + (211 - 168) * t)
								const b = Math.round(255 + (77 - 255) * t)
								return (
									<span
										key={index}
										className={s.waveBar}
										style={{
											'--delay': `${index * 0.03}s`,
											'--bar-r': r,
											'--bar-g': g,
											'--bar-b': b,
										}}
									/>
								)
							})}
						</div>
					</div>

					{showTranscript ? (
						<div className={s.transcriptCard}>
							<div className={s.transcriptLabel}>Live transcript</div>
							<div
								ref={transcriptScrollRef}
								className={s.transcriptScroll}
								data-note-transcript-scroll
							>
								{transcriptText ? (
									<p className={s.transcriptText}>{transcriptText}</p>
								) : null}
								{!transcriptText || isTranscribing ? (
									<p className={s.transcriptPlaceholder}>
										{isTranscribing ? 'Transcribing...' : 'Listening...'}
									</p>
								) : null}
							</div>
						</div>
					) : null}

					{showStudentRow ? (
						<div className={[s.studentRow, s.studentRowDetected].join(' ')}>
							<InitialAvatar
								name={student.name}
								size={30}
								avatarKey={student.avatarKey}
								avatarSource={student.avatarSource}
								photoUrl={student.photoUrl}
							/>
							<div className={s.studentMeta}>
								<span className={s.detectedName}>{student.name}</span>
								<span className={s.detectedSubline}>{studentSubline}</span>
							</div>
							<button
								type="button"
								className={s.editStudentButton}
								aria-label="Change student"
								onClick={() =>
									noteTakerStore.requestStudentAssignment('change')
								}
							>
								<PenIcon />
							</button>
						</div>
					) : null}

					{showMissingStudent ? (
						<div className={[s.studentRow, s.studentRowUnknown].join(' ')}>
							<QuestionCircleIcon className={s.questionIcon} />
							<div className={s.studentMeta}>
								<span className={s.detectedName}>No student found</span>
								<span className={s.detectedSubline}>
									Koda is still trying to identify the student
								</span>
							</div>
							<button
								type="button"
								className={s.editStudentButton}
								aria-label="Select student manually"
								onClick={() =>
									noteTakerStore.requestStudentAssignment('change')
								}
							>
								<PenIcon />
							</button>
						</div>
					) : null}

					{noteTakerStore.isPaused ? (
						<div className={s.pausedActions}>
							<button
								type="button"
								className={s.cancelButton}
								onClick={() => noteTakerStore.requestCancelRecording()}
							>
								Cancel
							</button>
							<button
								type="button"
								className={s.finishButton}
								disabled={isFinishDisabled}
								onClick={() =>
									student
										? noteTakerStore.finishRecording()
										: noteTakerStore.requestStudentAssignment('finish')
								}
							>
								Finish
							</button>
						</div>
					) : null}

					{isExpanded && !noteTakerStore.isPaused ? (
						<button
							type="button"
							className={s.finishNoteButton}
							disabled={isFinishDisabled}
							onClick={async () => {
								await noteTakerStore.pauseRecording()
								if (student) {
									await noteTakerStore.finishRecording()
								} else {
									noteTakerStore.requestStudentAssignment('finish')
								}
							}}
						>
							Finish note
						</button>
					) : null}
				</>
			)}
		</aside>
	)
})

export default NoteTakerDashboardWidget
